import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
} from "@mui/material";
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExportContainer,
  GridCsvExportMenuItem,
  GridPrintExportMenuItem,
  GridToolbarQuickFilter,
  GridToolbar,
} from "@mui/x-data-grid";

import { useTheme } from "@mui/material";
import { tokens } from "../theme";
import { Form, Alert } from "react-bootstrap";
import { apiUrl } from "../services/api";
import {
  Delete,
  Edit,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import Header from "../components/Header";
import { format } from "date-fns";

const AlertComponent = () => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [alertToDelete, setAlertToDelete] = useState(null);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [alerts, setAlerts] = useState([]);
  const [formData, setFormData] = useState({
    description: "",
    date_alert: "",
  });
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const fetchAlerts = async () => {
    try {
      const response = await axios.get(`${apiUrl}/alert`);
      const transformedAlerts = response.data.map((alert) => ({
        ...alert,
        date_alert: alert.date_alert ? formatDate(alert.date_alert) : "",
      }));
      setAlerts(transformedAlerts);
    } catch (err) {
      console.error("Error fetching alerts:", err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return format(date, "yyyy-MM-dd");
  };

  useEffect(() => {
    fetchAlerts();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleAddAlert = async (e) => {
    e.preventDefault();

    try {
      let response;
      if (editing) {
        response = await axios.put(`${apiUrl}/alert/${editId}`, formData);
      } else {
        response = await axios.post(`${apiUrl}/alert`, formData);
      }
      console.log("Server response:", response.data);
      await fetchAlerts();
      setEditing(false);
      setEditId(null);
      setFormData({
        description: "",
        date_alert: "",
      });
      setMessage(
        editing ? "Alert updated successfully" : "Alert added successfully"
      );
      setMessageType("success");
    } catch (err) {
      console.error("Error adding/updating alert:", err);
      setError(err.response?.data?.message || "Failed to add/update alert");
      setMessage("Failed to add/update alert");
      setMessageType("error");
    }
    setShowForm(false);
  };

  const handleEditAlert = (alert) => {
    setEditing(true);
    setEditId(alert.id_alert);
    setFormData({
      description: alert.description,
      date_alert: formatDate(alert.date_alert),
    });
    setShowForm(true);
  };

  const handleOpenConfirmDialog = (id) => {
    setAlertToDelete(id);
    setConfirmOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setConfirmOpen(false);
    setAlertToDelete(null);
  };

  const handleDeleteAlert = async () => {
    if (!alertToDelete) return;

    try {
      await axios.delete(`${apiUrl}/alert/${alertToDelete}`);
      fetchAlerts();
      setMessage("Alert deleted successfully");
      setMessageType("success");
    } catch (err) {
      console.error("Error deleting alert:", err);
      setMessage("Failed to delete alert");
      setMessageType("error");
    }
    setConfirmOpen(false);
  };

  const columns = [
    {
      field: "id_alert",
      headerName: "ID",
      width: 70,
      cellClassName: "name-column--cell2",
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      cellClassName: "name-column--cell",
    },
    {
      field: "date_alert",
      headerName: "Date d'alerte",
      width: 150,
      cellClassName: "name-column--cell",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      cellClassName: "actions",
      renderCell: (params) => (
        <>
          <GridActionsCellItem
            icon={<Edit />}
            label="Edit"
            onClick={() => handleEditAlert(params.row)}
            color="inherit"
          />
          <GridActionsCellItem
            icon={<Delete />}
            label="Delete"
            onClick={() => handleOpenConfirmDialog(params.row.id_alert)}
            color="inherit"
          />
        </>
      ),
    },
  ];

  return (
    <div>
      <Header title="Alertes" subtitle="Gestion des alertes." />
      {error && <Alert variant="danger">{error}</Alert>}
      {message && (
        <div
          className={`message ${messageType}`}
          style={{
            padding: "10px",
            marginBottom: "20px",
            borderRadius: "4px",
            backgroundColor: messageType === "success" ? "#d4edda" : "#f8d7da",
            color: messageType === "success" ? "#155724" : "#721c24",
            border: `1px solid ${
              messageType === "success" ? "#c3e6cb" : "#f5c6cb"
            }`,
          }}
        >
          {message}
        </div>
      )}
      <Dialog
        open={confirmOpen}
        onClose={handleCloseConfirmDialog}
        aria-labelledby="confirm-dialog-title"
      >
        <DialogTitle id="confirm-dialog-title">
          Confirmer la suppression
        </DialogTitle>
        <DialogContent>
          Êtes-vous sûr de vouloir supprimer cette alerte ?
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} color="primary">
            Annuler
          </Button>
          <Button onClick={handleDeleteAlert} color="secondary" autoFocus>
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      <Button
        onClick={() => setShowForm(!showForm)}
        variant="contained"
        sx={{
          color: colors.primary[500],
          backgroundColor: colors.greenAccent[500],
          ":hover": {
            color: colors.primary[500],
            backgroundColor: colors.greenAccent[300],
          },
          marginBottom: "20px",
        }}
      >
        {showForm ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
      </Button>
      {showForm && (
        <Form onSubmit={handleAddAlert}>
          <Box
            display="grid"
            gap="20px"
            gridTemplateColumns="repeat(2, minmax(0, 1fr))"
          >
            <TextField
              label="Description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              required
              fullWidth
              margin="normal"
            />
            <TextField
              label="Date d'alerte"
              name="date_alert"
              type="date"
              value={formData.date_alert}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Box>
          <Button
            variant="contained"
            type="submit"
            sx={{
              color: colors.primary[500],
              backgroundColor: colors.greenAccent[500],
              ":hover": {
                color: colors.primary[500],
                backgroundColor: colors.greenAccent[300],
              },
            }}
          >
            {editing ? "Modifier Alerte" : "Ajouter Alerte"}
          </Button>
        </Form>
      )}

      <Box
        m="40px 0 0 0"
        maxHeight="75vh"
        sx={{
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.greenAccent[300],
          },
          "& .name-column--cell2": {
            color: colors.redAccent[300],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.blueAccent[700],
            borderBottom: "none",
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
          "& .MuiDataGrid-root .MuiDataGrid-cell:focus": {
            outline: "none",
          },
          "& .MuiDataGrid-main": {
            overflow: "auto",
          },
        }}
      >
        <DataGrid
          rows={alerts}
          columns={columns}
          getRowId={(row) => row.id_alert}
          components={{ Toolbar: CustomToolbar }}
          pageSize={10}
          rowsPerPageOptions={[10, 20, 50]}
          disableExtendRowFullWidth={true}
          autoHeight={true}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
            },
          }}
        />
      </Box>
    </div>
  );
};

const csvOptions = { fileName: "Alertes", delimiter: ";", utf8WithBom: true };
const printOptions = {
  hideFooter: true,
  hideToolbar: true,
  includeCheckboxes: true,
};

function CustomExportButtons(props) {
  return (
    <GridToolbarExportContainer {...props}>
      <GridCsvExportMenuItem options={csvOptions} />
      <GridPrintExportMenuItem options={printOptions} />
    </GridToolbarExportContainer>
  );
}

function CustomToolbar(props) {
  return (
    <GridToolbarContainer {...props}>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <CustomExportButtons />
      <Box sx={{ marginLeft: "auto", pl: 0, pr: 0, pb: 0, pt: 0 }}>
        <GridToolbarQuickFilter />
      </Box>
    </GridToolbarContainer>
  );
}

export default AlertComponent;
